import ChartWidget from './ChartWidget.jsx';
import ScoreBadge from './ScoreBadge.jsx';
import ScoreBreakdown from './ScoreBreakdown.jsx';
import TargetTable from './TargetTable.jsx';
import { changeTone, formatCompact, formatDate, formatPercent, formatPrice } from '../lib/format.js';

/**
 * The main analysis view for one ticker: header, score gauge, chart, targets
 * and technical stats. Purely presentational — data arrives from `useStock`.
 *
 * @param {{stock: Record<string, any> | null, status: string, error: string | null,
 *   onSave: () => void, saving?: boolean, saved?: boolean, signedIn: boolean}} props
 */
export default function StockDashboard({
  stock,
  status,
  error,
  onSave,
  saving = false,
  saved = false,
  signedIn,
}) {
  if (status === 'loading') {
    return (
      <section className="panel px-4 py-16 text-center text-sm text-slate-500">
        Loading precomputed analysis…
      </section>
    );
  }

  if (status === 'error') {
    return (
      <section className="panel border-bear/40 px-4 py-8 text-center">
        <p className="text-sm text-bear">{error}</p>
        <p className="mt-1 text-xs text-slate-500">
          The daily pipeline has to have published this ticker first.
        </p>
      </section>
    );
  }

  if (!stock) {
    return (
      <section className="panel px-4 py-16 text-center text-sm text-slate-500">
        Search for an NSE ticker to load its Bull Score, targets and chart.
      </section>
    );
  }

  const indicators = stock.technical_indicators ?? {};

  return (
    <div className="flex flex-col gap-4">
      <section className="panel px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="font-mono text-xl text-slate-100">{stock.ticker}.NS</h2>
              <span className="pill border-terminal-600 bg-terminal-800 text-slate-300">
                {stock.sector ?? 'Unclassified'}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-400">{stock.company_name}</p>
            <p className="mt-2 flex flex-wrap items-baseline gap-3">
              <span className="font-mono text-2xl text-slate-50">{formatPrice(stock.close)}</span>
              <span className={`font-mono text-sm ${changeTone(indicators.return_1m_pct)}`}>
                {formatPercent(indicators.return_1m_pct)} <span className="text-slate-600">1M</span>
              </span>
              <span className="text-[11px] uppercase tracking-wider text-slate-500">
                session {formatDate(stock.date)}
              </span>
            </p>
          </div>

          <button
            type="button"
            className={saved ? 'btn-ghost' : 'btn-primary'}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : saved ? 'Saved ✓' : signedIn ? 'Save to history' : 'Sign in to save'}
          </button>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-[200px_minmax(0,1fr)]">
        <ScoreBadge
          score={stock.score}
          signal={stock.signal}
          detail={`Composite of trend, momentum, relative strength, volume, volatility and fundamentals`}
        />
        <ScoreBreakdown breakdown={stock.score_breakdown} />
      </div>

      <ChartWidget
        ohlcv={stock.ohlcv ?? []}
        indicators={indicators}
        symbolLabel={`${stock.ticker}.NS`}
      />

      <TargetTable targets={stock.targets ?? {}} currentPrice={stock.close} />

      <section className="panel">
        <header className="panel-heading">
          <h2 className="panel-title">Technical snapshot</h2>
          <span className="text-[11px] text-slate-500">{indicators.trend}</span>
        </header>
        <dl className="grid grid-cols-2 gap-4 px-4 py-4 sm:grid-cols-3 lg:grid-cols-4">
          <Stat label="RSI (14)" value={indicators.rsi14?.toFixed?.(1) ?? '—'} />
          <Stat label="MACD hist" value={indicators.macd_hist?.toFixed?.(2) ?? '—'} />
          <Stat label="ATR (14)" value={formatPrice(indicators.atr14)} />
          <Stat label="ATR % of price" value={formatPercent(indicators.atr_pct)} />
          <Stat label="EMA 20" value={formatPrice(indicators.ema20)} />
          <Stat label="EMA 50" value={formatPrice(indicators.ema50)} />
          <Stat label="EMA 200" value={formatPrice(indicators.ema200)} />
          <Stat label="Volume vs 20d" value={`${indicators.volume_ratio ?? '—'}×`} />
          <Stat label="52w high" value={formatPrice(indicators.high_52w)} />
          <Stat label="52w low" value={formatPrice(indicators.low_52w)} />
          <Stat label="From 52w high" value={formatPercent(indicators.pct_from_52w_high)} />
          <Stat label="Traded volume" value={formatCompact(indicators.volume)} />
          <Stat label="Return 3M" value={formatPercent(indicators.return_3m_pct)} />
          <Stat label="Return 6M" value={formatPercent(indicators.return_6m_pct)} />
          <Stat label="Return 1Y" value={formatPercent(indicators.return_1y_pct)} />
          <Stat
            label="Vs NIFTY 50 (3M)"
            value={`${formatPercent(indicators.relative_strength_3m_pct)}`}
          />
        </dl>
      </section>
    </div>
  );
}

/** Single key/value cell of the technical snapshot grid. */
function Stat({ label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="stat-label">{label}</dt>
      <dd className="stat-value text-base">{value}</dd>
    </div>
  );
}
